import axios from "axios";

// ============================================================================
// Backend access for the donor app.
//
// SECURITY: this file deliberately contains NO AWS credentials. Every API route
// is authorizationType NONE (the gateway never verified SigV4 signatures), so
// the old browser-side signing did nothing except ship IAM keys to the public.
// File uploads now go through a short-lived pre-signed S3 URL issued by the
// backend; the browser never touches S3 with credentials of its own.
// ============================================================================
export const useAws = () => {
  // For export
  let secretId = "ABC1";

  // API Gateway keys are throttling identifiers, not authentication.
  const x_api_key = "ZdbhxN9mUz5ligd30qQ3C4Y5KoYbXHA14aEX8Ogz";
  const x_api_key_cms = "51kSU4q3Fr6G6UdSka5E71yeu2tsj77Y7yi0Xwam";

  // Global constants
  const aws_region = "us-east-1";
  const aws_service = "execute-api";

  // Service
  const api_uri = "pnb_public_api" + (process.env.REACT_APP_ENV === "DEV" ? "/" : "_stage/");
  const api_gateway_host =
    process.env.REACT_APP_ENV === "DEV" ? `4kcdgeqkgf.${aws_service}.${aws_region}.amazonaws.com` : `boo7z0hhbk.${aws_service}.${aws_region}.amazonaws.com`;

  // Upload prefixes. DEV writes to its OWN prefixes so it can never touch a prod object;
  // a prod build automatically uses the prod paths.
  const supdocPrefix = process.env.REACT_APP_ENV === "DEV" ? "private/dev-supdoc/" : "private/supdoc/";

  //Host
  const apiHost = process.env.REACT_APP_ENV === "DEV" ? `https://${api_gateway_host}/dev_` : `https://${api_gateway_host}/`;

  /**
   *
   * @param {{HTTPRequestMethod, APIFunction, queryString, requestBody}} input
   * @returns
   */
  const awsHttp = (input) =>
    new Promise((resolve, reject) => {
      const { HTTPRequestMethod, APIFunction } = input;
      let { queryString, requestBody } = input;
      try {
        queryString = queryString ? convertToURIQueryString(queryString) : null;
        requestBody = requestBody ? JSON.stringify(requestBody) : null;

        let request_http = apiHost + api_uri + APIFunction;
        if (queryString) request_http += "?" + queryString;

        return axios
          .request({
            method: HTTPRequestMethod,
            url: request_http,
            data: requestBody,
            headers: { "Content-Type": "application/json", "x-api-key": x_api_key },
          })
          .then((res) => resolve(res))
          .catch((e) => reject(e));
      } catch (e) {
        return reject(e);
      }
    });

  /**
   * Upload a file to S3 without any AWS credentials in the browser:
   * 1) ask the backend for a short-lived pre-signed PUT URL for `key`,
   * 2) PUT the bytes straight to S3 (bucket CORS already allows browser PUT).
   * Same signature as before, so call sites are unchanged.
   */
  const uploadToS3 = (file, key) =>
    awsHttp({
      HTTPRequestMethod: "POST",
      APIFunction: "user/get_upload_url",
      requestBody: { key, content_type: file?.type || "application/octet-stream", content_length: file?.size || 0 },
    })
      .then((res) => {
        const data = res?.data || {};
        const body = data.body;
        const url = body && typeof body === "object" ? body.upload_url : null;
        if (!url) {
          const why = typeof body === "string" ? body : "could not get upload url";
          throw new Error(data.ErrorMessage ? `${why}: ${data.ErrorMessage}` : why);
        }
        return fetch(url, { method: "PUT", body: file, headers: { "Content-Type": file?.type || "application/octet-stream" } });
      })
      .then((r) => {
        if (!r.ok) throw new Error("upload failed: HTTP " + r.status);
        return { ok: true, key };
      });

  const convertToURIQueryString = (obj) => {
    let str = "";
    for (let key in obj) {
      str += `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}&`;
    }
    return str.slice(0, str.length - 1);
  };

  const getSecretId = () => secretId;
  const setSecretId = (str) => {
    secretId = str;
  };

  return { awsHttp, getSecretId, setSecretId, uploadToS3, supdocPrefix, x_api_key, x_api_key_cms };
};
